import axios from "axios";
import { AtvModel } from "hooks/useAtvModels";
import { ReservationFormState } from "hooks/useReservationForm";
import dayjs, { Dayjs } from 'dayjs';

export const calculateDailyPrice = (totalHours: number, selectedAtv: AtvModel): number => {
    const days = Math.max(1, Math.ceil(totalHours / 24));
    if (days >= 7 && selectedAtv.weekPrice) {
      const weeks = Math.floor(days / 7);
      return weeks * selectedAtv.weekPrice + (days % 7) * selectedAtv.price;
    }
    return days * selectedAtv.price;
};

export const calculatePrice = (
    fromDate: ReservationFormState['fromDate'],
    toDate: ReservationFormState['toDate'],
    fromHour: ReservationFormState['fromHour'],
    toHour: ReservationFormState['toHour'],
    atvModel: ReservationFormState['atvModel'],
    atvs: AtvModel[],
) => {
    const selectedAtv = atvs.find((atv) => atv.name === atvModel);
    if (!selectedAtv || !fromDate || !toDate || !fromHour || !toHour) {
      return 0;
    }

    // Weekend calculation: Friday 9:00 to Sunday 20:00
    const from = fromDate.hour(fromHour.hour()).minute(fromHour.minute());
    const to = toDate.hour(toHour.hour()).minute(toHour.minute());

    // Check for full weekend (Friday 9:00 to Sunday 20:00)
    const isFullWeekend =
      from.day() === 5 && from.hour() === 9 && // Friday 9:00
      to.day() === 0 && to.hour() === 20 && // Sunday 20:00
      to.diff(from, 'day') === 2;

    if (isFullWeekend && selectedAtv.weekendPrice) {
      return selectedAtv.weekendPrice;
    }

    // Calculate total hours
    const totalMinutes = to.diff(from, 'minute');
    const totalHours = totalMinutes / 60;

    // Jeśli ta sama data
    const isSameDay = from.isSame(to, 'day');
    if (isSameDay) {
      if (totalHours < 4) {
        // Price per hour
        return Math.ceil(totalHours) * (selectedAtv.pricePerHour || selectedAtv.price);
      } else {
        // Price per day
        return selectedAtv.price;
      }
    } else {
      return calculateDailyPrice(totalHours, selectedAtv);
    }
};

export const calculateReservationInfo = (
    fromDate: ReservationFormState['fromDate'],
    toDate: ReservationFormState['toDate'],
    fromHour: ReservationFormState['fromHour'],
    toHour: ReservationFormState['toHour'],
    selectedAtv: AtvModel | undefined,
) => {
    if (!selectedAtv || !fromDate || !toDate || !fromHour || !toHour) {
      return {
        isWeekend: false,
        days: 0,
        hours: 0,
        priceType: '',
      };
    }

    const from = fromDate.hour(fromHour.hour()).minute(fromHour.minute());
    const to = toDate.hour(toHour.hour()).minute(toHour.minute());

    const isFullWeekend =
      from.day() === 5 && from.hour() === 9 &&
      to.day() === 0 && to.hour() === 20 &&
      to.diff(from, 'day') === 2;

    const isSameDay = from.isSame(to, 'day');
    const totalMinutes = to.diff(from, 'minute');
    const totalHours = totalMinutes / 60;
    const days = to.startOf('day').diff(from.startOf('day'), 'day') + 1;

    let priceType = '';
    if (isFullWeekend && selectedAtv.weekendPrice) {
      priceType = 'weekend';
    } else if (days >= 7 && selectedAtv.weekPrice) {
      priceType = 'weekly';
    } else if (isSameDay) {
      priceType = totalHours < 4 ? 'hourly' : 'daily';
    } else {
      priceType = 'multi-day';
    }

    return {
      isWeekend: isFullWeekend,
      days: isSameDay ? 1 : days,
      hours: Math.round(totalHours),
      priceType,
    };
  };

export const getPayuRedirectUrl = async(reservationId: string): Promise<string | null> => {
    let attempts = 0;
    let redirectUri: string | null = null;

    while (attempts < 5 && !redirectUri) {
        try {
            const response = await axios.post(
            "https://europe-west3-atv-rent.cloudfunctions.net/getPayuRedirectUrl",
            { reservationId },
            { headers: { "Content-Type": "application/json" } }
            );
            // const response = { data: {
            //   redirectUri: null,
            // }} // leave it for now
            if (response.data && response.data.redirectUri) {
            redirectUri = response.data.redirectUri;
            break;
            }
        } catch (err) {
            console.log(err);
        }

        attempts++;

        if (!redirectUri) {
            await new Promise(res => setTimeout(res, 2000));
        }
    }

    return redirectUri;
}

export const getDeposit = (
    fromDate: ReservationFormState['fromDate'],
    toDate: ReservationFormState['toDate'],
    fromHour: ReservationFormState['fromHour'],
    toHour: ReservationFormState['toHour'],
    selectedAtv: AtvModel | undefined,
) => {
    if (selectedAtv && fromDate && toDate && fromHour && toHour) {
        const from = fromDate.tz('Europe/Warsaw').hour(fromHour.hour()).minute(fromHour.minute());
        const to = toDate.tz('Europe/Warsaw').hour(toHour.hour()).minute(toHour.minute());
        const totalMinutes = to.diff(from, 'minute');
        const totalHours = totalMinutes / 60;
        if (totalHours <= 3) {
             return selectedAtv.depositHour ?? 500;
        } else {
            return selectedAtv.depositDay ?? 1000;
        }
    }

    return 0;
}

export interface BusySlot {
  fromDate: string; // YYYY-MM-DD
  fromHour: string; // HH:mm
  toDate: string; // YYYY-MM-DD
  toHour: string; // HH:mm
}
// New response shape supports busy slots per ATV model
export interface BusySlotsResponse {
  busySlots: BusySlot[];
  busyByModel?: Record<string, BusySlot[]>;
}

// Simple in-memory cache for busy data
let busyDataCache: { data: BusySlotsResponse; fetchedAt: number } | null = null;
let busyDataPromise: Promise<BusySlotsResponse> | null = null;
const BUSY_DATA_TTL_MS = 60_000; // 60 seconds

// Fetch full busy data (global + per-model) with TTL cache
export const fetchBusyData = async (): Promise<BusySlotsResponse> => {
  const now = Date.now();
  if (busyDataCache && (now - busyDataCache.fetchedAt) < BUSY_DATA_TTL_MS) {
    return busyDataCache.data;
  }
  if (busyDataPromise) {
    return busyDataPromise;
  }
  busyDataPromise = (async () => {
    try {
      const res = await axios.get('https://europe-west3-atv-rent.cloudfunctions.net/getBusySlots');
      const data = res.data || {};

      // Normalizacja gdy backend zwraca tablicę busyByModel zamiast mapy
      if (Array.isArray(data.busyByModel)) {
        const map: Record<string, BusySlot[]> = {};
        for (const entry of data.busyByModel) {
          if (entry?.model) {
            if (!map[entry.model]) map[entry.model] = [];
            map[entry.model].push({
              fromDate: entry.fromDate,
              fromHour: entry.fromHour,
              toDate: entry.toDate,
              toHour: entry.toHour,
            });
          }
        }
        data.busyByModel = map;
      }

      const response: BusySlotsResponse = {
        busySlots: Array.isArray(data.busySlots) ? data.busySlots : [],
        busyByModel: data.busyByModel || undefined,
      };
      busyDataCache = { data: response, fetchedAt: Date.now() };
      return response;
    } catch (e) {
      console.error('Błąd pobierania slotów:', e);
      const fallback = { busySlots: [], busyByModel: undefined } as BusySlotsResponse;
      busyDataCache = { data: fallback, fetchedAt: Date.now() };
      return fallback;
    } finally {
      busyDataPromise = null;
    }
  })();
  return busyDataPromise;
};

// Backward-compatible helper: returns only global busy slots (deprecated)
export const getBusySlots = async (): Promise<BusySlot[]> => {
  const data = await fetchBusyData();
  return data.busySlots || [];
};

// Preferred helper: get busy slots for a specific model; falls back to global
export const getBusySlotsForModel = async (atvModel?: string | null): Promise<BusySlot[]> => {
  const data = await fetchBusyData();
  if (atvModel && data.busyByModel && data.busyByModel[atvModel]) {
    // MERGE: global + model-specific
    return [
      ...data.busyByModel[atvModel],
      ...(data.busySlots || [])
    ];
  }
  return data.busySlots || [];
};

// Helper: Find first available slot (centralized logic)
export interface AvailableSlot {
  fromDate: Dayjs;
  fromHour: Dayjs;
  toDate: Dayjs;
  toHour: Dayjs;
}

export const findFirstAvailableSlot = (
  busySlots: BusySlot[],
  startDate: Dayjs = dayjs(),
  minDurationHours: number = 3,
  businessStart: number = 9,
  businessEnd: number = 20
): AvailableSlot | null => {
  let date = startDate.clone().startOf('day');
  for (let d = 0; d < 31; d++) {
    const formattedDate = date.format('DD-MM-YYYY');
    // Check if day is fully busy
    const slots = busySlots
      .filter(slot => slot.fromDate === formattedDate)
      .map(slot => ({
        start: dayjs(`${slot.fromDate}T${slot.fromHour}`, 'DD-MM-YYYYTHH:mm'),
        end: dayjs(`${slot.toDate}T${slot.toHour}`, 'DD-MM-YYYYTHH:mm')
      }))
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());
    // Merge busy intervals
    const merged: { start: Dayjs, end: Dayjs }[] = [];
    for (const slot of slots) {
      if (!merged.length) merged.push({ ...slot });
      else {
        const last = merged[merged.length - 1];
        if (slot.start.isSameOrBefore(last.end)) last.end = dayjs.max(last.end, slot.end);
        else merged.push({ ...slot });
      }
    }
    // Find first available slot of minDurationHours
    let current = date.hour(businessStart).minute(0);
    const businessEndTime = date.hour(businessEnd).minute(0);
    for (const slot of merged) {
      if (slot.start.diff(current, 'minute') >= minDurationHours * 60) {
        // Found available slot
        return {
          fromDate: date,
          fromHour: current,
          toDate: date,
          toHour: current.add(minDurationHours, 'hour')
        };
      }
      if (slot.end.isAfter(current)) current = slot.end;
    }
    // Check after last busy slot
    if (businessEndTime.diff(current, 'minute') >= minDurationHours * 60) {
      return {
        fromDate: date,
        fromHour: current,
        toDate: date,
        toHour: current.add(minDurationHours, 'hour')
      };
    }
    // Next day
    date = date.add(1, 'day').startOf('day');
  }
  return null;
};
